import {keepPreviousData, queryOptions, useQuery} from "@tanstack/react-query";

import * as apiClient from "@/lib/api";
import {getGuideYear} from "@/lib/filters/date/date-utils";
import {topRatedAdvisersCountQueryOptions} from "@/lib/hooks/top-rated-advisers-count";
import {TopRatedAdviserData, TopRatedAdviserFetchParams, VerifiedAdvisersNumber} from "@/modules/top-rated-advisers/types";
import {DATA_TABLE_RECORD_FETCH_LIMIT, FIVE_MINUTES} from "@/utils/constants";
import {QUERY_KEY_TOP_RATED_ADVISERS_TYPE, QUERY_KEY_VERIFIED_ADVISERS_NUMBER} from "@/utils/keys";

const {VITE_QUERY_STALE_TIME} = import.meta.env

export const getDefaultFetchParams = (): TopRatedAdviserFetchParams => {
  const guideYear = getGuideYear()

  return {
    limit: DATA_TABLE_RECORD_FETCH_LIMIT,
    offset: 0,
    order_by: {
      column: 'adviser_name',
      direction: 'ASC',
    },
    guideYear: guideYear
  }
}

export const buildQueryParamsString = (fetchParams: TopRatedAdviserFetchParams): string => {
  const queryParams = new URLSearchParams()

  queryParams.append("limit", fetchParams.limit.toString())
  queryParams.append("offset", fetchParams.offset.toString())

  queryParams.append(`order_by[${fetchParams.order_by.column}]`, fetchParams.order_by.direction)
  queryParams.append(`filters[guideYear]`, String(fetchParams.guideYear))

  return queryParams.toString()
}

function fetchTopRatedAdvisers(): Promise<TopRatedAdviserData> {
  return apiClient.get(`/firm/top-rated-advisers?${buildQueryParamsString(getDefaultFetchParams())}`, true)
}

export const topRatedAdvisersQueryOptions = () =>
  queryOptions({
    queryKey: [QUERY_KEY_TOP_RATED_ADVISERS_TYPE],
    queryFn: fetchTopRatedAdvisers,
    placeholderData: keepPreviousData,
    staleTime: VITE_QUERY_STALE_TIME ? Number(VITE_QUERY_STALE_TIME) : FIVE_MINUTES,
  });

export const useTopRatedAdvisers = () => {
  const query = useQuery(topRatedAdvisersQueryOptions())

  return {
    data: query.data?.data ?? [],
    total: query.data?.aggregate_data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status
  }
}

function fetchVerifiedAdvisersNumber(): Promise<VerifiedAdvisersNumber> {
  return apiClient.get('/firm/dashboard/activity-levels/verified-professionals')
}

export const verifiedAdvisersNumberQueryOptions = () => queryOptions({
  queryKey: [QUERY_KEY_VERIFIED_ADVISERS_NUMBER],
  queryFn: fetchVerifiedAdvisersNumber,
  placeholderData: keepPreviousData
})

export const useVerifiedAdvisersNumber = () => {
  return useQuery(verifiedAdvisersNumberQueryOptions())
}

export const useTopRatedAdvisersCountForGuideYear = () => {
  return useQuery(topRatedAdvisersCountQueryOptions(getGuideYear()))
}
